// Iter68 — Reusable profile/listing card used across the admin
// (Partners, Customers, Affiliates, Drivers, Franchisees) so every
// list view feels like the Product Management gallery.
//
// Visual contract:
//   • Square/rounded image header (logo / avatar / default Lifted logo)
//   • Title + subtitle + (optional) chips row
//   • Stat triplets (label + value)
//   • Footer actions slot (Approve, Suspend, Edit, etc.)
//
// Designed to mimic /admin/products card density: 4-up on lg,
// 3-up on md, 2-up on sm, 1-up on xs.

import React from "react";
import { Link } from "react-router-dom";
import SafeImage, { LATM_FALLBACK_LOGO } from "@/components/SafeImage";

export function ProfileCard({
  image,
  imageFallback = LATM_FALLBACK_LOGO,
  imageBadge,            // small badge in top-right of image (e.g. "FEATURED")
  topRightBadge,         // small floating chip in card (e.g. "PENDING")
  title,
  subtitle,
  chips = [],            // [{ label, tone: 'green'|'purple'|'amber'|'rose'|undefined }]
  stats = [],            // [{ label, value }]
  meta,                  // small line under chips (e.g. "Anchorage, AK")
  footer,                // ReactNode (button row)
  to,                    // optional internal navigation
  href,                  // optional external link
  testId,
  onClick,
}) {
  const tone = (t) => ({
    green: "chip chip--green",
    purple: "chip chip--purple",
    amber: "chip chip--amber",
    rose: "chip chip--rose",
  }[t] || "chip");

  const card = (
    <div
      data-testid={testId}
      onClick={onClick}
      className="group relative flex flex-col rounded-2xl bg-lifted-surface border border-white/5 overflow-hidden hover:border-lifted-green/30 transition-colors"
    >
      <div className="relative aspect-[4/3] bg-black/30 overflow-hidden">
        <SafeImage
          src={image}
          fallback={imageFallback}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {imageBadge && (
          <span className="absolute top-2 left-2 chip chip--green !text-[10px]">
            {imageBadge}
          </span>
        )}
        {topRightBadge && (
          <span className="absolute top-2 right-2 chip !text-[10px] bg-black/60">
            {topRightBadge}
          </span>
        )}
      </div>

      <div className="flex-1 p-3 space-y-2">
        <div className="space-y-0.5">
          <div className="text-sm font-semibold text-lifted-text group-hover:text-lifted-green transition-colors uppercase font-bebas tracking-wider truncate">
            {title || "—"}
          </div>
          {subtitle && (
            <div className="text-[11px] text-lifted-dim truncate">{subtitle}</div>
          )}
          {meta && (
            <div className="text-[10px] text-lifted-mute truncate">{meta}</div>
          )}
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {chips.map((c, i) => c?.label && (
              <span key={i} className={`${tone(c.tone)} !text-[10px]`}>
                {c.label}
              </span>
            ))}
          </div>
        )}

        {stats.length > 0 && (
          <div className="grid grid-cols-3 gap-2 pt-1">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-anton text-base text-lifted-green leading-none">{s.value}</div>
                <div className="text-[9px] text-lifted-mute uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {footer && (
        <div className="px-3 pb-3 pt-1 border-t border-white/5 flex items-center gap-2 flex-wrap">
          {footer}
        </div>
      )}
    </div>
  );

  if (to) return <Link to={to} className="block">{card}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className="block">{card}</a>;
  return card;
}

export function ProfileCardGrid({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
    >
      {children}
    </div>
  );
}

export default ProfileCard;
